import { AxiosError } from 'axios';

interface ApiErrorResponse {
  message?: string;
  statusCode?: number;
  error?: string;
}

/**
 * Extract user-friendly error message from API errors.
 * Prioritizes server-provided messages, falls back to status-based messages.
 */
export function getErrorMessage(error: unknown, fallback?: string): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as ApiErrorResponse;

    // Use server-provided message if available (from ErrorMessage enum)
    if (data?.message) {
      return data.message;
    }

    // Handle specific status codes with user-friendly messages
    switch (error.response?.status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This action conflicts with existing data. Please refresh and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'A server error occurred. Our team has been notified.';
      case 502:
      case 503:
      case 504:
        return 'The service is temporarily unavailable. Please try again later.';
    }

    // Network error (no response)
    if (error.code === 'ERR_NETWORK') {
      return 'Unable to connect. Please check your internet connection.';
    }

    // Timeout error
    if (error.code === 'ECONNABORTED') {
      return 'The request took too long. Please try again.';
    }
  }

  // Handle generic Error objects
  if (error instanceof Error) {
    return error.message;
  }

  return fallback ?? 'An unexpected error occurred. Please try again.';
}

/**
 * Check if error is a specific HTTP status
 */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 404;
}

export function isUnauthorizedError(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 401;
}

export function isForbiddenError(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 403;
}

export function isNetworkError(error: unknown): boolean {
  return error instanceof AxiosError && error.code === 'ERR_NETWORK';
}

export function isValidationError(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 400;
}

/**
 * Get validation errors from a 400 response.
 * Returns an object with field names as keys and error messages as values.
 */
export function getValidationErrors(error: unknown): Record<string, string> | null {
  if (!isValidationError(error)) return null;

  const axiosError = error as AxiosError<{ message?: string | string[] }>;
  const message = axiosError.response?.data?.message;

  if (Array.isArray(message)) {
    // Handle class-validator style errors
    const errors: Record<string, string> = {};
    message.forEach((msg) => {
      // Try to extract field name from message like "field must be..."
      const match = msg.match(/^(\w+)\s/);
      if (match) {
        errors[match[1]] = msg;
      }
    });
    return Object.keys(errors).length > 0 ? errors : null;
  }

  return null;
}
